import { useMemo } from "react";
import { Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import Painel from "./Painel";
import KpiCard from "./KpiCard";
import GraficoBarrasMes from "./GraficoBarrasMes";
import TabelaAcordos from "./TabelaAcordos";
import { formatarNumero, formatarPercentualCurto, temaGrafico } from "./analiseUtils";
import {
    corAcordos, corEstado, derivados, fichasAcionadas, filtrarLinhas, formatarMoeda, formatarMoedaCurta, somarLinhas,
    totaisPorMes,
} from "./acordosUtils";

const formatoVariacao = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: "always" });

/** Variação em pontos percentuais; menorEhMelhor para a quebra (subir é ruim, mas a seta continua para cima). */
const variacaoPontos = (atual, anterior, menorEhMelhor = false) => {
    if (atual === null || atual === undefined || anterior === null || anterior === undefined) return null;
    const subiu = atual >= anterior;
    return {
        texto: `${formatoVariacao.format((atual - anterior) * 100)} p.p.`,
        positiva: menorEhMelhor ? atual <= anterior : subiu,
        subiu,
    };
};

/** Indicadores do escopo da tela (cliente, negociador ou coordenador) num período. */
function indicadoresDe(dados, atendimento, cliente, negociador, coordenador) {
    if (!dados) return null;
    const total = somarLinhas(filtrarLinhas(dados.linhas, cliente, negociador, coordenador));
    return { total, ...derivados(total, fichasAcionadas(atendimento, { dimensao: "total", cliente, negociador, coordenador })) };
}

/** Barra de estados (ex.: quitados / em andamento / cancelados), sempre com ícone, nome e número ao lado. */
function BarraEstados({ rotulo, partes, formatarValor }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);
    const total = partes.reduce((acc, p) => acc + p.valor, 0);

    return (
        <MDBox mb={2}>
            <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ display: "block", mb: 0.75 }}>
                {rotulo}
            </MDTypography>
            {total > 0 ? (
                <MDBox display="flex" gap="2px" sx={{ height: 14, borderRadius: 1, overflow: "hidden", background: tema.superficie }}>
                    {partes.filter(p => p.valor > 0).map(p => (
                        <MDBox key={p.nome} sx={{ flexGrow: p.valor, flexBasis: 0, background: corEstado(p.estado, darkMode) }} />
                    ))}
                </MDBox>
            ) : (
                <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.8 }}>Nada no período.</MDTypography>
            )}
            {total > 0 && partes.map(p => (
                <MDBox key={p.nome} display="flex" alignItems="center" justifyContent="space-between" mt={0.75} gap={1}>
                    <MDBox display="flex" alignItems="center" gap={0.75}>
                        <Icon sx={{ color: corEstado(p.estado, darkMode), fontSize: "1rem !important" }}>{p.icone}</Icon>
                        <MDTypography variant="caption" color="text">{p.nome}</MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                        {formatarValor(p)} · {formatarPercentualCurto(p.valor / total)}
                    </MDTypography>
                </MDBox>
            ))}
        </MDBox>
    );
}

/**
 * Seção "Acordos e Recebimentos" no fim da Análise de Atendimento: o que não está nos cards e na tabela de desempenho
 * (adimplência, cancelados, desconto, negociado × recebido por mês, situação dos acordos e das parcelas e o resultado por
 * negociador, coordenador, cliente ou mês). Cliente, negociador e coordenador filtram só a tela.
 */
export default function AcordosRecebimentos({
    dados, dadosAnterior, atendimento, atendimentoAnterior, cliente, negociador, coordenador, rotuloVariacao, dimensaoInicial,
}) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const linhas = useMemo(() => filtrarLinhas(dados?.linhas, cliente, negociador, coordenador), [dados, cliente, negociador, coordenador]);
    const atual = useMemo(
        () => indicadoresDe(dados, atendimento, cliente, negociador, coordenador),
        [dados, atendimento, cliente, negociador, coordenador]
    );
    const anterior = useMemo(
        () => indicadoresDe(dadosAnterior, atendimentoAnterior, cliente, negociador, coordenador),
        [dadosAnterior, atendimentoAnterior, cliente, negociador, coordenador]
    );
    const porMes = useMemo(() => totaisPorMes(linhas, dados?.meses || []), [linhas, dados]);

    if (!atual) return null;
    const { total } = atual;
    const emAndamento = Math.max(total.acordos - total.acordosQuitados - total.acordosCancelados, 0);
    const naoPagas = Math.max(total.valorVencidas - total.valorVencidasPagas, 0);
    const parcelasNaoPagas = Math.max(total.parcelasVencidas - total.vencidasPagas, 0);
    const [anoFim, mesFim, diaFim] = (dados.dataFinal || "").split("-");

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={4}>
                <KpiCard
                    icone="event_available"
                    cor={corEstado("bom", darkMode)}
                    rotulo="Adimplência"
                    valor={atual.adimplencia === null ? "–" : formatarPercentualCurto(atual.adimplencia)}
                    detalhe={`${formatarNumero(total.vencidasPagas)} de ${formatarNumero(total.parcelasVencidas)} parcelas vencidas pagas (por valor)`}
                    detalheFixo
                    variacao={variacaoPontos(atual.adimplencia, anterior?.adimplencia)}
                    rotuloVariacao={rotuloVariacao}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <KpiCard
                    icone="cancel"
                    cor={corEstado("ruim", darkMode)}
                    rotulo="Acordos Cancelados"
                    valor={atual.quebra === null ? "–" : formatarPercentualCurto(atual.quebra)}
                    detalhe={`${formatarNumero(total.acordosCancelados)} dos acordos do período · ${formatarMoeda(total.valorCancelados)}`}
                    detalheFixo
                    variacao={variacaoPontos(atual.quebra, anterior?.quebra, true)}
                    rotuloVariacao={rotuloVariacao}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <KpiCard
                    icone="sell"
                    cor={corAcordos("negociado", darkMode)}
                    rotulo="Desconto Concedido"
                    valor={atual.descontoPct === null ? "–" : formatarPercentualCurto(atual.descontoPct)}
                    detalhe={`${formatarMoeda(total.desconto)} · ${formatarNumero(total.acordosAVista)} à vista, ${formatarNumero(total.acordos - total.acordosAVista)} parcelados`}
                    detalheFixo
                    variacao={variacaoPontos(atual.descontoPct, anterior?.descontoPct, true)}
                    rotuloVariacao={rotuloVariacao}
                />
            </Grid>

            <Grid item xs={12} lg={7}>
                <Painel rotulo="Negociado × Recebido por mês">
                    <GraficoBarrasMes
                        meses={dados.meses}
                        series={[
                            { rotulo: "Negociado (acordos formalizados)", dados: porMes.map(m => m.valorAcordos), cor: corAcordos("negociado", darkMode) },
                            { rotulo: "Recebido", dados: porMes.map(m => m.recebidoAcordos + m.recebidoAvulso), cor: corAcordos("recebido", darkMode) },
                        ]}
                        formatar={formatarMoeda}
                        formatarCurto={formatarMoedaCurta}
                    />
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.8 }}>
                        Negociado no mês da formalização; recebido no mês do pagamento (inclui parcelas de acordos de meses anteriores).
                    </MDTypography>
                </Painel>
            </Grid>
            <Grid item xs={12} lg={5}>
                <Painel rotulo="Situação dos acordos e das parcelas">
                    <BarraEstados
                        rotulo="Acordos formalizados no período, situação de hoje (quantidade)"
                        formatarValor={(p) => formatarNumero(p.valor)}
                        partes={[
                            { nome: "Quitados", valor: total.acordosQuitados, estado: "bom", icone: "check_circle" },
                            { nome: "Em andamento", valor: emAndamento, estado: "andamento", icone: "schedule" },
                            { nome: "Cancelados", valor: total.acordosCancelados, estado: "ruim", icone: "cancel" },
                        ]}
                    />
                    <BarraEstados
                        rotulo="Parcelas que venceram no período (valor)"
                        formatarValor={(p) => `${formatarMoeda(p.valor)} (${formatarNumero(p.qtd)})`}
                        partes={[
                            { nome: "Pagas", valor: total.valorVencidasPagas, qtd: total.vencidasPagas, estado: "bom", icone: "check_circle" },
                            { nome: "Não pagas", valor: naoPagas, qtd: parcelasNaoPagas, estado: "ruim", icone: "error" },
                        ]}
                    />
                    {total.parcelasAVencer > 0 && (
                        <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                            A vencer até {diaFim}/{mesFim}/{anoFim}: {formatarNumero(total.parcelasAVencer)} parcelas · {formatarMoeda(total.valorAVencer)}
                        </MDTypography>
                    )}
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        Recebido no período: {formatarMoeda(atual.recebido)} em {formatarNumero(total.pagamentos)} pagamentos
                        ({formatarMoeda(total.recebidoAcordos)} de acordos · {formatarMoeda(total.recebidoAvulso)} de baixa avulsa)
                    </MDTypography>
                </Painel>
            </Grid>

            <Grid item xs={12}>
                <Painel rotulo="Resultado por negociador, coordenador, cliente ou mês">
                    <TabelaAcordos
                        linhas={linhas}
                        meses={dados.meses}
                        atendimento={atendimento}
                        cliente={cliente}
                        negociador={negociador}
                        coordenador={coordenador}
                        dimensaoInicial={dimensaoInicial}
                    />
                </Painel>
            </Grid>
        </Grid>
    );
}
